import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  ElementRef,
  signal,
  ViewChild,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  FormsModule,
} from '@angular/forms';
import { ProfileFacadeService } from '@sh/facades/profile-facade.service';
import { IAvatarCustomization } from '@sh/interfaces/i-avatar-customization';
import { AccountDeletionService } from '@sh/services/account-deletion/account-deletion.service';
import { AuthService } from '@sh/services/auth/auth.service';
import {
  ContributionsService,
  IContributionResponse,
} from '@sh/services/contributions/contributions.service';
import { ResourcesService } from '@sh/resources/services/resources.service';
import { IResource } from '@sh/resources/interfaces/i-resource';
import { ProfileService } from '@sh/services/profile/profile.service';
import { ToastService } from '@sh/services/toast/toast.service';
import { generateRandomAlias } from '@sh/utils/alias-generator';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  finalize,
  map,
  of,
  shareReplay,
  startWith,
  switchMap,
  take,
} from 'rxjs';

interface ActivityState<T> {
  items: T[];
  total: number;
  page: number;
  totalPages: number;
  loading: boolean;
  error: boolean;
}

const emptyActivity = <T>(page = 1): ActivityState<T> => ({
  items: [],
  total: 0,
  page,
  totalPages: 0,
  loading: false,
  error: false,
});

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, RouterLink],
})
export class ProfileComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly resourcesService = inject(ResourcesService);
  profile$ = this.profileFacade.profile$;
  authCredentials$ = this._authService.authCredentials$;
  private readonly profileId$ = this.profile$.pipe(
    map((profile) => profile?.id ?? null),
    distinctUntilChanged(),
  );
  readonly resourcePage$ = new BehaviorSubject(1);
  readonly contributionPage$ = new BehaviorSubject(1);
  activeActivity: 'resources' | 'contributions' = 'resources';
  readonly pageSize = 12;
  saving = signal(false);

  readonly resources$ = combineLatest([
    this.profileId$,
    this.resourcePage$,
  ]).pipe(
    switchMap(([userId, page]) =>
      userId
        ? this.resourcesService
            .listResources({ page, pageSize: this.pageSize, userIds: [userId] })
            .pipe(
              map((response) => ({
                ...emptyActivity<IResource>(page),
                items: response.data.items,
                total: response.data.meta.total,
                totalPages: response.data.meta.total_pages,
              })),
              startWith({ ...emptyActivity<IResource>(page), loading: true }),
              catchError(() =>
                of({ ...emptyActivity<IResource>(page), error: true }),
              ),
            )
        : of(emptyActivity<IResource>()),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly contributions$ = combineLatest([
    this.profileId$,
    this.contributionPage$,
  ]).pipe(
    switchMap(([userId, page]) =>
      userId
        ? this.contributionsService
            .getUserContributions(userId, page, this.pageSize)
            .pipe(
              map((response) => ({
                ...emptyActivity<IContributionResponse>(page),
                items: response.data.items,
                total: response.data.meta.total,
                totalPages: response.data.meta.total_pages,
              })),
              startWith({
                ...emptyActivity<IContributionResponse>(page),
                loading: true,
              }),
              catchError(() =>
                of({
                  ...emptyActivity<IContributionResponse>(page),
                  error: true,
                }),
              ),
            )
        : of(emptyActivity<IContributionResponse>()),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  contributionLink(contribution: IContributionResponse): string[] | null {
    const content = contribution.content;
    if (content.type === 'Resource') return ['/resources', content.id];
    if (content.type === 'Book') return ['/books/detail', content.id];
    if (content.type === 'BookEdition' && content.bookId) {
      return [
        '/books/detail',
        content.bookId,
        'bookEditions/detail',
        content.id,
      ];
    }
    return null;
  }

  displayLabel(value?: string): string {
    const option = [
      ...this.predefinedPowers,
      ...this.predefinedWeaknesses,
    ].find((item) => item.value === value);
    return option?.label || value?.replace(/-/g, ' ') || '';
  }

  @ViewChild('deleteAccountModal')
  deleteAccountModal!: ElementRef<HTMLDialogElement>;

  /** Word the user must type to enable the irreversible delete action. */
  readonly confirmWord = 'DELETE';
  confirmText = '';
  deleting = signal(false);

  availableAvatars = [
    'batguy.webp',
    'superhero-africa-black-orange.webp',
    'superhero-africa-blue-terracotta.webp',
    'superhero-africa-ochre-indigo.webp',
    'superhero-africa-red-gold-green.webp',
    'superhero-crimson-mint.webp',
    'superhero-girl-blue-yellow.webp',
    'superhero-girl-emerald-peach.webp',
    'superhero-girl-magenta-aqua.webp',
    'superhero-girl-silver-teal.webp',
    'superhero-gold-navy.webp',
    'superhero-teal-coral.webp',
    'superhero-violet-orange.webp',
  ];

  isEditMode = false;
  avatarData: IAvatarCustomization | null = null;
  profileForm = new FormGroup({
    firstName: new FormControl(''),
    lastName: new FormControl(''),
    email: new FormControl('', [Validators.required, Validators.email]),
    aliasName: new FormControl('', [Validators.required]),
    avatarImage: new FormControl('batguy.webp'),
    // Appearance
    head: new FormControl('classic'),
    body: new FormControl('athletic'),
    legs: new FormControl('boots'),
    mask: new FormControl('domino'),
    hair: new FormControl('short'),
    skinTone: new FormControl('medium'),
    primaryColor: new FormControl('#1e40af'), // Blue
    secondaryColor: new FormControl('#dc2626'), // Red
    pattern: new FormControl('solid'),
    // Background
    origin: new FormControl('student'),
    motivation: new FormControl('knowledge'),
    backstory: new FormControl(''),
    // Superpowers
    primaryPower: new FormControl('super-intelligence'),
    secondaryPower: new FormControl(''),
    weakness: new FormControl(''),
    // Accessories
    cape: new FormControl(true),
    emblem: new FormControl('book'),
    gadgets: new FormControl<string[]>([]),
  });

  predefinedPowers = [
    { value: 'super-intelligence', label: 'Super intelligence' },
    { value: 'speed-reading', label: 'Speed reading' },
    { value: 'photographic-memory', label: 'Photographic memory' },
    { value: 'laser-focus', label: 'Laser focus' },
    { value: 'time-manipulation', label: 'Time management' },
    { value: 'infinite-stamina', label: 'Infinite stamina' },
  ];

  predefinedWeaknesses = [
    { value: 'procrastination', label: 'Procrastination' },
    { value: 'social-media', label: 'Social media' },
    { value: 'snacking', label: 'Endless snacking' },
    { value: 'perfectionism', label: 'Perfectionism' },
    { value: 'daydreaming', label: 'Daydreaming' },
  ];

  constructor(
    private _authService: AuthService,
    private profileFacade: ProfileFacadeService,
    private contributionsService: ContributionsService,
    private accountDeletion: AccountDeletionService,
    private profileService: ProfileService,
    private toastService: ToastService,
  ) {}

  get currentAvatarImage(): string {
    if (this.isEditMode) {
      return this.profileForm.value.avatarImage || 'batguy.webp';
    }
    return this.avatarData?.avatarImage || 'batguy.webp';
  }

  getRandomAvatar(): string {
    const randomIndex = Math.floor(
      Math.random() * this.availableAvatars.length,
    );
    return this.availableAvatars[randomIndex];
  }

  ngOnInit() {
    // Parse avatar data whenever profile updates
    this.profile$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((profile) => {
        if (profile?.avatarJsonData && profile.avatarJsonData !== '{}') {
          try {
            this.avatarData = JSON.parse(
              profile.avatarJsonData,
            ) as IAvatarCustomization;
            if (!this.avatarData.avatarImage) {
              this.avatarData.avatarImage = this.getRandomAvatar();
            }
          } catch (e) {
            console.warn('Failed to parse avatar data', e);
            this.avatarData = { avatarImage: this.getRandomAvatar() } as any;
          }
        } else if (profile) {
          this.avatarData = { avatarImage: this.getRandomAvatar() } as any;
        } else {
          this.avatarData = null;
        }
      });
  }

  signOut() {
    this._authService.signOut();
  }
  openDeleteModal() {
    this.confirmText = '';
    this.deleteAccountModal.nativeElement.showModal();
  }

  closeDeleteModal() {
    if (this.deleting()) return; // don't allow closing mid-request
    this.deleteAccountModal.nativeElement.close();
  }

  async onConfirmDelete() {
    if (this.confirmText !== this.confirmWord || this.deleting()) return;

    this.deleting.set(true);
    try {
      // On success the service signs out and routes away, destroying this view.
      await this.accountDeletion.deleteAccount();
    } catch {
      this.deleting.set(false);
      this.toastService.showToast(
        { message: 'We couldn’t delete your account. Please try again.' },
        'error',
      );
    }
  }

  enableEditMode() {
    this.profile$.pipe(take(1)).subscribe((profile) => {
      if (profile) {
        // Parse avatar data from profile.avatarJsonData if it exists
        let avatarData: IAvatarCustomization | null = null;
        try {
          if (profile.avatarJsonData && profile.avatarJsonData !== '{}') {
            avatarData = JSON.parse(
              profile.avatarJsonData,
            ) as IAvatarCustomization;
          }
        } catch (e) {
          console.warn(
            'Failed to parse avatar data from profile.avatarJsonData',
            e,
          );
        }

        if (!avatarData) {
          avatarData = {
            aliasName: '',
            avatarImage: this.avatarData?.avatarImage || this.getRandomAvatar(),
            appearance: {
              head: 'classic',
              body: 'athletic',
              legs: 'boots',
              mask: 'domino',
              hair: 'short',
              skinTone: 'medium',
              costume: {
                primaryColor: '#1e40af',
                secondaryColor: '#dc2626',
                pattern: 'solid',
              },
            },
            background: {
              origin: 'student',
              motivation: 'knowledge',
              backstory: '',
            },
            superpowers: {
              primary: 'super-intelligence',
              secondary: '',
              weakness: '',
            },
            accessories: {
              cape: true,
              emblem: 'book',
              gadgets: [],
            },
          };
        }

        if (!avatarData.avatarImage) {
          avatarData.avatarImage =
            this.avatarData?.avatarImage || this.getRandomAvatar();
        }

        // Generate a random alias if none exists
        const existingAlias = avatarData?.aliasName;
        const aliasToUse = existingAlias || generateRandomAlias();

        // Enable the form controls explicitly
        this.profileForm.enable();
        this.profileForm.patchValue({
          firstName: profile.firstName || '',
          lastName: profile.lastName || '',
          email: profile.email || '',
          aliasName: aliasToUse,
          avatarImage: avatarData.avatarImage,
          // Appearance
          head: avatarData?.appearance?.head || 'classic',
          body: avatarData?.appearance?.body || 'athletic',
          legs: avatarData?.appearance?.legs || 'boots',
          mask: avatarData?.appearance?.mask || 'domino',
          hair: avatarData?.appearance?.hair || 'short',
          skinTone: avatarData?.appearance?.skinTone || 'medium',
          primaryColor:
            avatarData?.appearance?.costume?.primaryColor || '#1e40af',
          secondaryColor:
            avatarData?.appearance?.costume?.secondaryColor || '#dc2626',
          pattern: avatarData?.appearance?.costume?.pattern || 'solid',
          // Background
          origin: avatarData?.background?.origin || 'student',
          motivation: avatarData?.background?.motivation || 'knowledge',
          backstory: avatarData?.background?.backstory || '',
          // Superpowers
          primaryPower:
            avatarData?.superpowers?.primary || 'super-intelligence',
          secondaryPower: avatarData?.superpowers?.secondary || '',
          weakness: avatarData?.superpowers?.weakness || '',
          // Accessories
          cape: avatarData?.accessories?.cape ?? true,
          emblem: avatarData?.accessories?.emblem || 'book',
          gadgets: avatarData?.accessories?.gadgets || [],
        });
        this.isEditMode = true;
      }
    });
  }

  cancelEdit() {
    this.isEditMode = false;
    this.profileForm.reset();
  }

  generateNewAlias() {
    const newAlias = generateRandomAlias();
    this.profileForm.patchValue({ aliasName: newAlias });
  }

  saveProfile() {
    if (this.saving()) return;
    if (!this.profileForm.valid) {
      this.profileForm.markAllAsTouched();
      this.toastService.showToast(
        { message: 'Please fill in all fields correctly' },
        'error',
      );
      return;
    }

    // Build avatar customization data
    const avatarData: IAvatarCustomization = {
      aliasName: this.profileForm.value.aliasName || '',
      avatarImage: this.profileForm.value.avatarImage || 'batguy.webp',
      appearance: {
        head: this.profileForm.value.head || 'classic',
        body: this.profileForm.value.body || 'athletic',
        legs: this.profileForm.value.legs || 'boots',
        mask: this.profileForm.value.mask || 'domino',
        hair: this.profileForm.value.hair || 'short',
        skinTone: this.profileForm.value.skinTone || 'medium',
        costume: {
          primaryColor: this.profileForm.value.primaryColor || '#1e40af',
          secondaryColor: this.profileForm.value.secondaryColor || '#dc2626',
          pattern: this.profileForm.value.pattern || 'solid',
        },
      },
      background: {
        origin: this.profileForm.value.origin || 'student',
        motivation: this.profileForm.value.motivation || 'knowledge',
        backstory: this.profileForm.value.backstory || undefined,
      },
      superpowers: {
        primary: this.profileForm.value.primaryPower || 'super-intelligence',
        secondary: this.profileForm.value.secondaryPower || undefined,
        weakness: this.profileForm.value.weakness || undefined,
      },
      accessories: {
        cape: this.profileForm.value.cape || false,
        emblem: this.profileForm.value.emblem || 'book',
        gadgets: this.profileForm.value.gadgets || [],
      },
    };

    const updateData = {
      firstName: this.profileForm.value.firstName || undefined,
      lastName: this.profileForm.value.lastName || undefined,
      email: this.profileForm.value.email || undefined,
      request: {
        data: JSON.stringify(avatarData),
      },
    };

    this.saving.set(true);
    this.profileService
      .updateProfile(updateData)
      .pipe(
        finalize(() => this.saving.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.toastService.showToast(
            { message: 'Profile updated successfully!' },
            'success',
          );
          this.isEditMode = false;
          this.profileFacade.refreshProfile();
        },
        error: (error) => {
          let message = 'Failed to update profile';
          if (error.error?.message) {
            message = error.error.message;
          }
          this.toastService.showToast({ message }, 'error');
        },
      });
  }
}
